/**
 * GraphQL Client
 * Centralized GraphQL client with environment-aware configuration
 * Handles all GraphQL queries and mutations with proper error handling
 */

import { API_CONFIG, getGraphQLHeaders } from '@/config/envConfig';

export interface GraphQLResponse<T = any> {
  data?: T;
  errors?: Array<{
    message: string;
    locations?: Array<{ line: number; column: number }>;
    path?: string[];
  }>;
}

export interface GraphQLRequest {
  query: string;
  variables?: Record<string, any>;
  operationName?: string;
  method?: 'POST' | 'GET';
}

export interface GraphQLClientResponse<T = any> {
  data?: T;
  errors?: string[];
  success: boolean;
  status: number;
}

class GraphQLClient {
  private readonly endpoint: string;
  private readonly websocketUrl: string;
  private defaultTimeout: number = 30000; // 30 seconds

  constructor() {
    this.endpoint = API_CONFIG.GRAPHQL_ENDPOINT;
    this.websocketUrl = API_CONFIG.GRAPHQL_WEBSOCKET_URL;
  }

  /**
   * Execute GraphQL query or mutation
   */
  async request<T = any>(
    request: GraphQLRequest,
    hasuraToken: string,
    timeout: number = this.defaultTimeout
  ): Promise<GraphQLClientResponse<T>> {
    try {
      const method = request.method || 'POST';
      let url = this.endpoint;
      let fetchOptions: RequestInit = {
        method,
        headers: getGraphQLHeaders(hasuraToken),
        signal: AbortSignal.timeout(timeout)
      };

      if (method === 'GET') {
        // Encode query and variables as URL parameters for GET requests
        const params = new URLSearchParams();
        params.append('query', request.query);
        if (request.variables) {
          params.append('variables', JSON.stringify(request.variables));
        }
        if (request.operationName) {
          params.append('operationName', request.operationName);
        }
        url = `${this.endpoint}?${params.toString()}`;
      } else {
        // POST request with body
        fetchOptions.body = JSON.stringify({
          query: request.query,
          variables: request.variables,
          operationName: request.operationName
        });
      }

      const response = await fetch(url, fetchOptions);

      const result: GraphQLResponse<T> = await response.json();

      if (!response.ok) {
        return {
          success: false,
          status: response.status,
          errors: [`HTTP ${response.status}: ${response.statusText}`]
        };
      }

      if (result.errors && result.errors.length > 0) {
        // Check for JWT claims error
        const hasJwtClaimsError = result.errors.some(e => 
          e.message.includes('jwt-invalid-claims') || 
          e.message.includes("claims key: 'https://hasura.io/jwt/claims' not found")
        );
        
        if (hasJwtClaimsError) {
          console.error('HASURA TOKEN ERROR: Token missing required JWT claims structure');
          console.error('Backend must generate token with https://hasura.io/jwt/claims in payload');
        }
        
        console.error('GraphQL error:', {
          messages: result.errors.map(e => e.message),
          query: request.query,
          variables: request.variables,
        });
        
        return {
          success: false,
          status: response.status,
          errors: result.errors.map(error => error.message),
          data: result.data
        };
      }

      return {
        success: true,
        status: response.status,
        data: result.data
      };
    } catch (error) {
      console.error('GraphQL request failed:', error);
      return {
        success: false,
        status: 0,
        errors: [error instanceof Error ? error.message : 'Unknown GraphQL error']
      };
    }
  }

  /**
   * Execute a query
   */
  async query<T = any>(
    query: string,
    variables?: Record<string, any>,
    hasuraToken?: string,
    operationName?: string,
    method?: 'POST' | 'GET'
  ): Promise<GraphQLClientResponse<T>> {
    // Fallback to main auth token if no Hasura token provided
    const fallbackToken = (() => { try { return localStorage.getItem('hasuraToken') || localStorage.getItem('authToken'); } catch { return null; } })();
    const token = hasuraToken || fallbackToken || '';
    if (!token) {
      return {
        success: false,
        status: 401,
        errors: ['Token is required for GraphQL queries']
      };
    }

    return this.request<T>(
      { query, variables, operationName, method },
      token
    );
  }

  /**
   * Execute a mutation
   */
  async mutate<T = any>(
    mutation: string,
    variables?: Record<string, any>,
    hasuraToken?: string,
    operationName?: string
  ): Promise<GraphQLClientResponse<T>> {
    // Fallback to main auth token if no Hasura token provided
    const fallbackToken = (() => { try { return localStorage.getItem('hasuraToken') || localStorage.getItem('authToken'); } catch { return null; } })();
    const token = hasuraToken || fallbackToken || '';
    if (!token) {
      return {
        success: false,
        status: 401,
        errors: ['Token is required for GraphQL mutations']
      };
    }

    return this.request<T>(
      { query: mutation, variables, operationName },
      token
    );
  }

  /**
   * Get WebSocket URL for subscriptions
   */
  getWebSocketUrl(): string {
    return this.websocketUrl;
  }

  /**
   * Get GraphQL endpoint URL
   */
  getEndpoint(): string {
    return this.endpoint;
  }
}

// Export singleton instance
export const graphqlClient = new GraphQLClient();
export default graphqlClient;